import { useRef, useState } from "react";
import { SHOWCASE, type ShowcaseItem } from "../content/showcase";
import { nearestSlideIndex } from "../utils/carousel";
import { Icon } from "./Icon";
import { PhoneFrame } from "./PhoneFrame";
import "./styles/ShowcaseCarousel.css";

export function ShowcaseCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const lastIndex = SHOWCASE.length - 1;

  function slideOffsets(track: HTMLDivElement): number[] {
    return Array.from(track.children).map((slide) => (slide as HTMLElement).offsetLeft);
  }

  function handleScroll() {
    const track = trackRef.current;
    if (track) setActiveIndex(nearestSlideIndex(slideOffsets(track), track.scrollLeft));
  }

  function goToSlide(index: number) {
    const track = trackRef.current;
    if (track) track.scrollTo({ left: slideOffsets(track)[index], behavior: "smooth" });
  }

  function handlePrevious() {
    goToSlide(Math.max(0, activeIndex - 1));
  }

  function handleNext() {
    goToSlide(Math.min(lastIndex, activeIndex + 1));
  }

  return (
    <section className="section showcase container" aria-roledescription="carousel" aria-labelledby="showcase-title">
      <div className="section__head section__head--centered">
        <p className="eyebrow">See it in action</p>
        <h2 id="showcase-title" className="section__title">A closer look at SENfully</h2>
      </div>

      <div className="showcase__controls">
        <div className="showcase__topics" role="group" aria-label="Choose a topic">
          {SHOWCASE.map((item, index) => (
            <TopicButton key={item.id} label={item.eyebrow} isActive={index === activeIndex} index={index} onSelect={goToSlide} />
          ))}
        </div>

        <div className="showcase__arrows">
          <button type="button" className="showcase__arrow" aria-label="Previous" onClick={handlePrevious} disabled={activeIndex === 0}>
            <span className="showcase__arrow-icon showcase__arrow-icon--back"><Icon name="arrow-right" size={18} /></span>
          </button>
          <button type="button" className="showcase__arrow" aria-label="Next" onClick={handleNext} disabled={activeIndex === lastIndex}>
            <Icon name="arrow-right" size={18} />
          </button>
        </div>
      </div>

      <div ref={trackRef} className="showcase__track" onScroll={handleScroll}>
        {SHOWCASE.map((item, index) => (
          <Slide key={item.id} item={item} position={`${index + 1} of ${SHOWCASE.length}`} />
        ))}
      </div>
    </section>
  );
}

interface TopicButtonProps {
  label: string;
  isActive: boolean;
  index: number;
  onSelect: (index: number) => void;
}

function TopicButton({ label, isActive, index, onSelect }: TopicButtonProps) {
  function handleClick() {
    onSelect(index);
  }

  return (
    <button type="button" className={isActive ? "showcase__topic showcase__topic--active" : "showcase__topic"} aria-current={isActive} onClick={handleClick}>
      {label}
    </button>
  );
}

function Slide({ item, position }: { item: ShowcaseItem; position: string }) {
  return (
    <article className="showcase__slide" role="group" aria-roledescription="slide" aria-label={`${item.eyebrow}, ${position}`}>
      <div className="showcase__visual">
        {item.screens.map((screen, index) => (
          <PhoneFrame
            key={screen.src}
            className={index === 0 ? "showcase__phone showcase__phone--front" : "showcase__phone showcase__phone--back"}
            src={screen.src}
            alt={screen.alt}
          />
        ))}
      </div>

      <div className="showcase__copy">
        <h3 className="showcase__title">{item.title}</h3>
        <p className="showcase__description">{item.description}</p>
        <ul className="showcase__points">
          {item.points.map((point) => (
            <li key={point}>
              <span className="showcase__tick"><Icon name="check" size={14} /></span>
              {point}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}
